import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';

export const GROWTH_SERVICE_TRIGGER_TYPES = [
  'ChurnSignal',
  'Manual',
  'ComplaintLinked',
  'ExpansionOpportunity'
] as const;

export const GROWTH_SERVICE_CHANNELS = [
  'email',
  'sms',
  'whatsapp',
  'telegram',
  'in-app',
  'push',
  'voice'
] as const;

export const GROWTH_SUCCESS_PLAN_STATUSES = [
  'Draft',
  'Active',
  'Completed',
  'Archived'
] as const;

export const GROWTH_RETENTION_APPROVAL_STATES = [
  'Draft',
  'Approved',
  'Rejected',
  'ExecutedDeferred'
] as const;

export const growthSuccessGoalFields: StructuredJsonFieldDef[] = [
  { key: 'title', type: 'string', labelKey: 'growth.service.successPlanDetail.goals.fields.title', required: true },
  { key: 'description', type: 'textarea', labelKey: 'growth.service.successPlanDetail.goals.fields.description' },
  { key: 'metric', type: 'string', labelKey: 'growth.service.successPlanDetail.goals.fields.metric' }
];

export const growthSuccessMilestoneFields: StructuredJsonFieldDef[] = [
  { key: 'title', type: 'string', labelKey: 'growth.service.successPlanDetail.milestones.fields.title', required: true },
  { key: 'dueDate', type: 'string', labelKey: 'growth.service.successPlanDetail.milestones.fields.dueDate' },
  {
    key: 'status',
    type: 'select',
    labelKey: 'growth.service.successPlanDetail.milestones.fields.status',
    options: [
      { value: 'Planned', labelKey: 'growth.service.successPlanDetail.milestones.status.Planned' },
      { value: 'InProgress', labelKey: 'growth.service.successPlanDetail.milestones.status.InProgress' },
      { value: 'Done', labelKey: 'growth.service.successPlanDetail.milestones.status.Done' },
      { value: 'Skipped', labelKey: 'growth.service.successPlanDetail.milestones.status.Skipped' }
    ]
  }
];

export const growthRetentionPlaybookFields: StructuredJsonFieldDef[] = [
  {
    key: 'priority',
    type: 'select',
    labelKey: 'growth.service.retentionPlayDetail.playbook.fields.priority',
    options: [
      { value: 'low', labelKey: 'growth.service.retentionPlayDetail.playbook.priority.low' },
      { value: 'medium', labelKey: 'growth.service.retentionPlayDetail.playbook.priority.medium' },
      { value: 'high', labelKey: 'growth.service.retentionPlayDetail.playbook.priority.high' }
    ]
  },
  {
    key: 'actionReviewSuccessPlan',
    type: 'boolean',
    labelKey: 'growth.service.retentionPlayDetail.playbook.fields.actionReviewSuccessPlan'
  },
  {
    key: 'actionConsentOutreach',
    type: 'boolean',
    labelKey: 'growth.service.retentionPlayDetail.playbook.fields.actionConsentOutreach'
  },
  {
    key: 'actionOfferIncentive',
    type: 'boolean',
    labelKey: 'growth.service.retentionPlayDetail.playbook.fields.actionOfferIncentive'
  },
  {
    key: 'note',
    type: 'textarea',
    labelKey: 'growth.service.retentionPlayDetail.playbook.fields.note'
  }
];

export function emptyGoalPayloadJson(): string {
  return JSON.stringify({ title: '', description: '', metric: '' });
}

export function emptyMilestonePayloadJson(): string {
  return JSON.stringify({ title: '', dueDate: '', status: 'Planned' });
}

export function parseJsonArrayPayloads(json: string | null | undefined, emptyItem: () => string): string[] {
  if (!json?.trim()) {
    return [];
  }
  try {
    const parsed = JSON.parse(json) as unknown;
    if (!Array.isArray(parsed)) {
      return [emptyItem()];
    }
    return parsed.map(item => JSON.stringify(item ?? {}));
  } catch {
    return [];
  }
}

export function serializeJsonArrayPayloads(payloads: string[]): string {
  const items = payloads
    .map(payload => {
      try {
        return JSON.parse(payload) as Record<string, unknown>;
      } catch {
        return null;
      }
    })
    .filter((item): item is Record<string, unknown> => !!item)
    .filter(item => String(item['title'] ?? '').trim().length > 0);
  return JSON.stringify(items);
}

export function emptyPlaybookFormJson(): string {
  return JSON.stringify({
    priority: 'medium',
    actionReviewSuccessPlan: true,
    actionConsentOutreach: true,
    actionOfferIncentive: false,
    note: ''
  });
}

export function playbookApiJsonToFormJson(apiJson: string | null | undefined): string {
  try {
    const parsed = (apiJson?.trim() ? JSON.parse(apiJson) : {}) as Record<string, unknown>;
    const actions = Array.isArray(parsed['suggestedActions'])
      ? parsed['suggestedActions'].map(item => String(item))
      : [];
    return JSON.stringify({
      priority: typeof parsed['priority'] === 'string' ? parsed['priority'] : 'medium',
      actionReviewSuccessPlan: actions.includes('review_success_plan') || parsed['actionReviewSuccessPlan'] === true,
      actionConsentOutreach: actions.includes('consent_gated_outreach') || parsed['actionConsentOutreach'] === true,
      actionOfferIncentive: actions.includes('offer_retention_incentive') || parsed['actionOfferIncentive'] === true,
      note: typeof parsed['note'] === 'string' ? parsed['note'] : ''
    });
  } catch {
    return emptyPlaybookFormJson();
  }
}

export const growthTwinWhatIfFields: StructuredJsonFieldDef[] = [
  { key: 'budgetDeltaPercent', type: 'number', labelKey: 'growth.twin.scenarios.form.budgetDeltaPercent' },
  { key: 'channelLiftPercent', type: 'number', labelKey: 'growth.twin.scenarios.form.channelLiftPercent' },
  { key: 'priceDeltaPercent', type: 'number', labelKey: 'growth.twin.scenarios.form.priceDeltaPercent' },
  { key: 'segmentLiftPercent', type: 'number', labelKey: 'growth.twin.scenarios.form.segmentLiftPercent' }
];

export const growthTwinSimulationDisplayFields: StructuredJsonFieldDef[] = [
  { key: 'leadDelta', type: 'number', labelKey: 'growth.twin.scenarioDetail.simulation.fields.leadDelta' },
  { key: 'conversionDelta', type: 'number', labelKey: 'growth.twin.scenarioDetail.simulation.fields.conversionDelta' },
  { key: 'revenueDelta', type: 'number', labelKey: 'growth.twin.scenarioDetail.simulation.fields.revenueDelta' },
  { key: 'clvDelta', type: 'number', labelKey: 'growth.twin.scenarioDetail.simulation.fields.clvDelta' },
  { key: 'confidenceLow', type: 'number', labelKey: 'growth.twin.scenarioDetail.simulation.fields.confidenceLow' },
  { key: 'confidenceHigh', type: 'number', labelKey: 'growth.twin.scenarioDetail.simulation.fields.confidenceHigh' },
  { key: 'summary', type: 'textarea', labelKey: 'growth.twin.scenarioDetail.simulation.fields.summary' }
];

export const growthTwinAdvisoryDisplayFields: StructuredJsonFieldDef[] = [
  { key: 'advisoryOnly', type: 'boolean', labelKey: 'growth.twin.scenarioDetail.advisory.fields.advisoryOnly' },
  { key: 'forAgents', type: 'boolean', labelKey: 'growth.twin.scenarioDetail.advisory.fields.forAgents' },
  { key: 'forOptimizer', type: 'boolean', labelKey: 'growth.twin.scenarioDetail.advisory.fields.forOptimizer' },
  { key: 'recommendedAction', type: 'string', labelKey: 'growth.twin.scenarioDetail.advisory.fields.recommendedAction' },
  { key: 'summary', type: 'textarea', labelKey: 'growth.twin.scenarioDetail.advisory.fields.summary' }
];

export function emptyTwinWhatIfPayloadJson(): string {
  return JSON.stringify({
    budgetDeltaPercent: 10,
    channelLiftPercent: 5,
    priceDeltaPercent: 0,
    segmentLiftPercent: 0
  });
}

export function playbookFormJsonToApiJson(formJson: string): string {
  let parsed: Record<string, unknown> = {};
  try {
    parsed = JSON.parse(formJson || '{}') as Record<string, unknown>;
  } catch {
    parsed = {};
  }

  const suggestedActions: string[] = [];
  if (parsed['actionReviewSuccessPlan'] === true) {
    suggestedActions.push('review_success_plan');
  }
  if (parsed['actionConsentOutreach'] === true) {
    suggestedActions.push('consent_gated_outreach');
  }
  if (parsed['actionOfferIncentive'] === true) {
    suggestedActions.push('offer_retention_incentive');
  }

  return JSON.stringify({
    advisory: true,
    priority: typeof parsed['priority'] === 'string' ? parsed['priority'] : 'medium',
    note: typeof parsed['note'] === 'string' ? parsed['note'] : '',
    suggestedActions
  });
}
